import axios from 'axios';

import { getAuthToken } from '../../utils/auth';
import apiClient from '../apiClient';

export interface OmniscientAgentResponse {
  content: string;
  agent_used?: string;
  confidence?: number;
  actions?: ActionButton[];
  timestamp: string;
  error?: boolean;
}

export interface ActionButton {
  label: string;
  primary: boolean;
  action: string;
  data?: any;
}

export interface OmniscientAgentRequest {
  user_input: string;
  session_id: string;
  priority: 'low' | 'medium' | 'high';
  context: {
    source: string;
    timestamp: string;
  };
}

class OmniscientAgentService {
  private baseURL: string;
  private sessionId: string;

  constructor() {
    this.baseURL = process.env.REACT_APP_OMNISCIENT_AGENT_URL || '/api';
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getAuthToken(): string | null {
    return getAuthToken();
  }

  async sendMessage(message: string): Promise<OmniscientAgentResponse> {
    try {
      const authToken = this.getAuthToken();

      if (!authToken) {
        throw new Error('Authentication token not found');
      }

      const request: OmniscientAgentRequest = {
        user_input: message,
        session_id: this.sessionId,
        priority: 'high',
        context: {
          source: 'legal_assistant',
          timestamp: new Date().toISOString(),
        },
      };

      console.log('🎤 OmniscientAgent: Sending request:', request);

      const response = await axios.post(`${this.baseURL}/api/agent-swarm`, request, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        timeout: 30000, // 30 second timeout
      });

      if (!response.data) {
        throw new Error('Empty response from Omniscient Agent');
      }

      const data = response.data;

      return {
        content: data.response || data.content || 'No response content received',
        agent_used: data.agent_used,
        confidence: data.confidence_score,
        actions: this.generateActionButtons(data),
        timestamp: new Date().toLocaleTimeString(),
      };
    } catch (error) {
      console.error('🎤 OmniscientAgent: API error:', error);

      return {
        content:
          "I apologize, but I'm having trouble connecting to the AI system. Please try again.",
        error: true,
        timestamp: new Date().toLocaleTimeString(),
      };
    }
  }

  private generateActionButtons(response: any): ActionButton[] {
    const actions: ActionButton[] = [];

    // Generate context-appropriate action buttons based on response content
    const content = response.response || response.content || '';

    if (content.toLowerCase().includes('contract')) {
      actions.push(
        { label: 'Auto-redraft', primary: true, action: 'redraft' },
        { label: 'Assign Paralegal', primary: false, action: 'assign' },
        { label: 'Notify Client', primary: false, action: 'notify' }
      );
    }

    if (content.toLowerCase().includes('compliance')) {
      actions.push(
        { label: 'Generate Report', primary: true, action: 'report' },
        { label: 'Schedule Review', primary: false, action: 'schedule' }
      );
    }

    if (content.toLowerCase().includes('case') || content.toLowerCase().includes('litigation')) {
      actions.push(
        { label: 'Research Precedents', primary: true, action: 'research' },
        { label: 'Create Timeline', primary: false, action: 'timeline' },
        { label: 'Contact Expert', primary: false, action: 'expert' }
      );
    }

    if (content.toLowerCase().includes('risk') || content.toLowerCase().includes('liability')) {
      actions.push(
        { label: 'Risk Assessment', primary: true, action: 'risk_assessment' },
        { label: 'Mitigation Plan', primary: false, action: 'mitigation' }
      );
    }

    // Default actions if no specific context is detected
    if (actions.length === 0) {
      actions.push(
        { label: 'Save to Notes', primary: false, action: 'save' },
        { label: 'Follow Up', primary: false, action: 'followup' }
      );
    }

    return actions;
  }

  async handleAction(action: ActionButton): Promise<void> {
    try {
      console.log('🎤 OmniscientAgent: Handling action:', action);
      const payload = { action: action.action, data: action.data || {} };
      await apiClient.post('/api/ai/tool/execute', payload);
    } catch (error) {
      console.error('🎤 OmniscientAgent: Action handling error:', error);
    }
  }
}

export const omniscientAgentService = new OmniscientAgentService();
